import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import apiCoreAdapter from "../../hooks/apiCoreAdapter";
import {
  generatePatientSummary,
  getPatientSummaryState,
  PatientSummary,
} from "../../hooks/api/patientSummary";
import { clearDraft, loadDraft, saveDraft } from "./meetRules";

type Note = {
  session_id: string;
  date: string;
  notes: string;
  process_id: string;
  psychologist_name: string;
  psychologist_discharged: boolean;
};

const fmt = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const SUMMARY_TITLES: Record<keyof PatientSummary["sections"], string> = {
  motivoConsulta: "Motivo de consulta",
  evolucionProceso: "Evolución del proceso",
  estadoActual: "Estado actual",
  riesgosYRecursos: "Riesgos y recursos",
  pendientesContinuidad: "Pendientes de continuidad",
};

/**
 * Solapa "Historia clínica": registro de ESTA sesión (borrador automático en el navegador; "Guardar registro" lo confirma), resumen por IA a
 * pedido e historial de registros anteriores. Es el mismo registro (uno por sesión) que "Agregar registro" en Pacientes: se guarda con
 * el mismo endpoint y la misma clave (id de la sesión), y antes de guardar se compara con lo que hay para no pisar un cambio hecho en otra pantalla.
 */
export function ClinicalTab({
  sessionId,
  processId,
  readOnly,
}: {
  sessionId: string;
  processId: string;
  readOnly: boolean;
}) {
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const baseline = useRef("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [summary, setSummary] = useState<PatientSummary | null>(null);
  const [summaryBusy, setSummaryBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const items = (await apiCoreAdapter.getPatientSessionNotes(
        processId,
      )) as Note[];
      setNotes(items);
      setLoadError(false);
      return items;
    } catch {
      setLoadError(true);
      return null;
    }
  }, [processId]);

  useEffect(() => {
    void load().then((items) => {
      const current =
        items?.find((n) => n.session_id === sessionId)?.notes ?? "";
      baseline.current = current;
      setText(loadDraft(sessionId) ?? current);
    });
    getPatientSummaryState(processId)
      .then((s) => setSummary(s.summary))
      .catch(() => undefined);
  }, [load, processId, sessionId]);

  const onChange = (v: string) => {
    setText(v);
    setMessage(null);
    saveDraft(sessionId, v === baseline.current ? "" : v);
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Si el registro cambió desde otra pantalla desde que lo abrimos, se avisa antes de pisarlo
      const fresh = await load();
      const now = fresh?.find((n) => n.session_id === sessionId)?.notes ?? "";
      if (
        now !== baseline.current &&
        now !== text &&
        !window.confirm(
          "Este registro cambió desde otra pantalla (por ejemplo, desde Pacientes). ¿Querés reemplazarlo por lo que escribiste acá?",
        )
      ) {
        setMessage(
          "No se guardó: el registro tiene cambios hechos en otra pantalla.",
        );
        return;
      }
      await apiCoreAdapter.updateSessionNote(sessionId, text);
      baseline.current = text;
      clearDraft(sessionId);
      await load();
    } catch {
      setMessage("No pudimos guardar el registro. Tu borrador sigue acá.");
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setSummaryBusy(true);
    try {
      setSummary(await generatePatientSummary(processId));
    } catch {
      setMessage("No pudimos generar el resumen.");
    } finally {
      setSummaryBusy(false);
    }
  };

  const previous = (notes ?? []).filter(
    (n) => n.session_id !== sessionId && n.notes?.trim(),
  );
  const dirty = text !== baseline.current;

  return (
    <div className="space-y-4 text-sm">
      <section>
        <h3 className="mb-1 font-semibold">Registro de esta sesión</h3>
        <textarea
          className="h-40 w-full rounded-lg border p-2"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          placeholder="Escribí el registro de la sesión…"
          aria-label="Registro de esta sesión"
        />
        <div className="mt-1 flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={readOnly || saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? "Guardando…" : "Guardar registro"}
          </Button>
          {dirty && !readOnly && (
            <span className="text-xs text-gray-500">Borrador sin guardar</span>
          )}
        </div>
        {readOnly && (
          <p className="mt-1 text-xs text-amber-700">
            Estás en modo impersonación: no se guardan registros.
          </p>
        )}
        {message && (
          <p role="alert" className="mt-1 text-xs text-red-600">
            {message}
          </p>
        )}
      </section>

      <details className="rounded-lg border p-2">
        <summary className="cursor-pointer font-medium">
          Resumen del paciente
        </summary>
        <div className="mt-2 space-y-2">
          {summary ? (
            (
              Object.keys(
                SUMMARY_TITLES,
              ) as (keyof PatientSummary["sections"])[]
            ).map((k) =>
              summary.sections[k]?.length ? (
                <div key={k}>
                  <p className="font-medium">{SUMMARY_TITLES[k]}</p>
                  <ul className="list-disc pl-5">
                    {summary.sections[k].map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : null,
            )
          ) : (
            <p className="text-gray-500">Todavía no hay un resumen generado.</p>
          )}
          {summary && (
            <p className="text-xs text-gray-500">{summary.disclaimer}</p>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={summaryBusy || readOnly}
            onClick={() => void generate()}
          >
            {summaryBusy
              ? "Generando…"
              : summary
                ? "Regenerar"
                : "Generar resumen"}
          </Button>
        </div>
      </details>

      <section>
        <h3 className="mb-1 font-semibold">Registros anteriores</h3>
        {notes === null && !loadError && (
          <p className="text-gray-500">Cargando…</p>
        )}
        {loadError && (
          <p className="text-red-600">No pudimos cargar el historial.</p>
        )}
        {notes && previous.length === 0 && (
          <p className="text-gray-500">No hay registros anteriores.</p>
        )}
        {previous.map((n, i) => (
          <details
            key={n.session_id}
            open={i === 0}
            className="mb-2 rounded-lg border p-2"
          >
            <summary className="cursor-pointer">
              {fmt(n.date)} · {n.psychologist_name}
            </summary>
            <p className="mt-1 whitespace-pre-wrap">{n.notes}</p>
          </details>
        ))}
      </section>
    </div>
  );
}
